import React, { useState, useEffect, useRef } from 'react';
import styles from './TakeActionModal.module.scss';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@apollo/client/react';
import { getAuthApolloClient } from '@jwp/ott-common/src/graphql/ApolloClient';
import {
    GET_REPORT_REASONS,
    GetReportReasonsData,
    GetReportReasonsVariables,
    ReportReason,
    TAKE_MODERATION_ACTION,
    TakeModerationActionData,
    TakeModerationActionVariables,
    ModerationActionType,
} from '@jwp/ott-common/src/graphql/query/reportModeration.queries';
import classNames from 'classnames';
import { showToast } from '@jwp/ott-common/src/stores/ToastStore';
import { PATH_CHANNEL_REPORTS } from '@jwp/ott-common/src/paths';

// SVG Icon Imports
import CloseChannelIcon from '@jwp/ott-theme/assets/icons/Close_channel.svg?react';
import CloseTagIcon from '@jwp/ott-theme/assets/icons/close_grey.svg?react';
import InfoIcon from '@jwp/ott-theme/assets/icons/Info_grey.svg?react';
import ChevronUpIcon from '@jwp/ott-theme/assets/icons/chevron_up.svg?react';
import ChevronDownIcon from '@jwp/ott-theme/assets/icons/chevron_down.svg?react';
import WarningIcon from '@jwp/ott-theme/assets/icons/warning.svg?react';
import CloseRedIcon from '@jwp/ott-theme/assets/icons/close_red.svg?react';
import { useNavigate } from 'react-router-dom';

type PopupType = 'warn_creator' | 'remove_content' | 'suspend_channel' | 'delete_channel';

type ActionModalProps = {
    onClose?: () => void;
    onSubmit?: () => void;
    rcId: string | undefined;
    popupType?: PopupType | string;
};

const SUSPENSION_DURATIONS = [
    { label: '1 Day', value: 1 },
    { label: '7 Days', value: 7 },
    { label: '30 Days', value: 30 },
    { label: 'Permanent', value: null },
];

const TakeActionModal: React.FC<ActionModalProps> = ({
    onClose,
    onSubmit,
    rcId,
    popupType = 'warn_creator',
}) => {
    const { t } = useTranslation(['user', 'common']);
    const navigate = useNavigate()
    const [isOpen, setIsOpen] = useState(false);
    const [selectedTypeCodes, setSelectedTypeCodes] = useState<string[]>([]);
    const [message, setMessage] = useState('');
    const [selectedDuration, setSelectedDuration] = useState<number | null>(null);

    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fetch report reasons only for warn_creator
    const { data: reportReasonsData, loading: loadingReasons, error: reasonsError } = useQuery<
        GetReportReasonsData,
        GetReportReasonsVariables
    >(GET_REPORT_REASONS, {
        client: getAuthApolloClient(),
        variables: {},
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
        skip: popupType !== 'warn_creator',
    });

    // Apollo Mutation for Moderation Action
    const [takeModerationAction, { loading: submitting }] = useMutation<
        TakeModerationActionData,
        TakeModerationActionVariables
    >(TAKE_MODERATION_ACTION, {
        client: getAuthApolloClient(),
    });

    const reportReasons: ReportReason[] = reportReasonsData?.getReportReasons || [];

    // Prevent background scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleType = (rrCode: string) => {
        setSelectedTypeCodes((prev) =>
            prev.includes(rrCode) ? prev.filter((code) => code !== rrCode) : [...prev, rrCode]
        );
    };

    const removeType = (e: React.MouseEvent, rrCode: string) => {
        e.stopPropagation();
        setSelectedTypeCodes((prev) => prev.filter((code) => code !== rrCode));
    };

    const isFormValid = () => {
        if (popupType === 'warn_creator') {
            return selectedTypeCodes.length > 0 && message.trim().length > 0;
        }
        return message.trim().length > 0;
    };

    // Helper to map popupType string to ModerationActionType Enum
    const getActionTypeEnum = (): ModerationActionType => {
        switch (popupType) {
            case 'remove_content':
                return ModerationActionType.REMOVE_CONTENT;
            case 'suspend_channel':
                return ModerationActionType.SUSPEND_CHANNEL;
            case 'delete_channel':
                return ModerationActionType.DELETE_CHANNEL;
            case 'warn_creator':
            default:
                return ModerationActionType.WARN_CREATOR;
        }
    };

    const handleSubmit = async () => {
        if (!isFormValid() || !rcId) return;

        const actionType = getActionTypeEnum();

        const inputPayload = {
            rcId: rcId,
            actionType: actionType,
            notes: message.trim(),
            ...(popupType === 'warn_creator' && { reasonIds: selectedTypeCodes }),
            ...(popupType === 'suspend_channel' && { durationDays: selectedDuration }),
        };

        try {
            const { data } = await takeModerationAction({
                variables: {
                    input: inputPayload,
                },
            });

            if (data?.takeModerationAction) {
                showToast(
                    t('common:report_submitted', {
                        defaultValue: 'Action taken successfully',
                    }),
                    'success'
                );

                navigate(`${PATH_CHANNEL_REPORTS}?tab=reviewed`)

                if (onSubmit) {
                    onSubmit();
                }
                if (onClose) onClose();
            }
        } catch (error) {
            console.error('Failed to take moderation action:', error);
        }
    };

    // Title configuration by popup type
    const getModalTitle = () => {
        switch (popupType) {
            case 'remove_content':
                return t('user:remove_content.title', 'Remove Content');
            case 'suspend_channel':
                return t('user:suspend_channel.title', 'Suspend Creator Channel');
            case 'delete_channel':
                return t('user:delete_channel.title', 'Delete Creator Channel');
            case 'warn_creator':
            default:
                return t('user:warn_creator.title', 'Warn Creator');
        }
    };

    // Button text & styling
    const getSubmitButtonConfig = () => {
        switch (popupType) {
            case 'remove_content':
                return { label: t('common:remove', 'Remove'), styleClass: styles.removeBtn };
            case 'suspend_channel':
                return { label: t('common:suspend', 'Suspend'), styleClass: styles.suspendBtn };
            case 'delete_channel':
                return { label: t('common:delete', 'Delete'), styleClass: styles.deleteBtn };
            case 'warn_creator':
            default:
                return { label: t('user:warn_creator.send_warning', 'Send Warning'), styleClass: styles.warnBtn };
        }
    };

    const selectedReasons = reportReasons.filter((reason) =>
        selectedTypeCodes.includes(reason.rrId)
    );
    const visibleTags = selectedReasons.slice(0, 3);
    const remainingCount = selectedReasons.length - 3;
    const submitConfig = getSubmitButtonConfig();

    const isChannelActionPopup =
        popupType === 'suspend_channel' || popupType === 'delete_channel';

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className={styles.header}>
                    <h2 className={styles.title}>{getModalTitle()}</h2>
                    {onClose && (
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={onClose}
                            aria-label="Close modal"
                        >
                            <CloseChannelIcon className={styles.icon} />
                        </button>
                    )}
                </div>

                {/* Content Body */}
                <div className={styles.content}>

                    {/* Warning / Alert Banners */}
                    {popupType === 'remove_content' && (
                        <div className={styles.warningBanner}>
                            <div className={styles.bannerHeader}>
                                <WarningIcon className={styles.warningIcon} />
                                <p className={styles.bannerText}>
                                    {t(
                                        'user:remove_content.banner_text',
                                        'This video will be permanently removed from the platform and will no longer be accessible to viewers. This action cannot be undone.'
                                    )}
                                </p>
                            </div>
                        </div>
                    )}

                    {popupType === 'suspend_channel' && (
                        <div className={styles.warningBanner}>
                            <div className={styles.bannerHeader}>
                                <WarningIcon className={styles.warningIcon} />
                                <p className={styles.bannerText}>
                                    {t(
                                        'user:suspend_channel.banner_text',
                                        'The creator will be temporarily restricted from uploading new content or going live until the suspension period ends.'
                                    )}
                                </p>
                            </div>
                        </div>
                    )}

                    {popupType === 'delete_channel' && (
                        <div className={styles.warningBanner}>
                            <div className={styles.bannerHeader}>
                                <WarningIcon className={styles.warningIcon} />
                                <span className={styles.deleteText}>
                                    {t('user:delete_channel.banner_title', 'This action is permanent and cannot be undone.')}
                                </span>
                            </div>
                            <ul className={styles.bannerList}>
                                <li>
                                    <CloseRedIcon className={styles.closeRedIcon} />
                                    {t('user:delete_channel.item1', 'Creator channel permanently deleted')}
                                </li>
                                <li>
                                    <CloseRedIcon className={styles.closeRedIcon} />
                                    {t('user:delete_channel.item2', 'All videos and reels removed')}
                                </li>
                                <li>
                                    <CloseRedIcon className={styles.closeRedIcon} />
                                    {t('user:delete_channel.item3', 'Playlist and saved content deleted')}
                                </li>
                                <li>
                                    <CloseRedIcon className={styles.closeRedIcon} />
                                    {t('user:delete_channel.item4', 'All subscribers lose this channel')}
                                </li>
                            </ul>
                        </div>
                    )}

                    {/* Warning Type Dropdown (Only for Warn Creator) */}
                    {popupType === 'warn_creator' && (
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>
                                {t('user:warn_creator.warning_type', 'Warning Type')}
                                <span className={styles.required}>*</span>
                            </label>

                            <div className={styles.dropdownContainer} ref={dropdownRef}>
                                <div
                                    className={`${styles.selectTrigger} ${isOpen ? styles.triggerOpen : ''}`}
                                    onClick={() => setIsOpen(!isOpen)}
                                >
                                    {selectedTypeCodes.length === 0 ? (
                                        <span className={styles.placeholder}>-Select type-</span>
                                    ) : (
                                        <div className={styles.tagsWrapper}>
                                            {visibleTags.map((reason) => (
                                                <span key={reason.rrId} className={styles.tag}>
                                                    {reason.rrLabel}
                                                    <button
                                                        type="button"
                                                        className={styles.removeTagBtn}
                                                        onClick={(e) => removeType(e, reason.rrId)}
                                                    >
                                                        <CloseTagIcon className={styles.iconTag} />
                                                    </button>
                                                </span>
                                            ))}
                                            {remainingCount > 0 && (
                                                <span className={styles.tag}>+{remainingCount} More</span>
                                            )}
                                        </div>
                                    )}
                                    {isOpen ? (
                                        <ChevronUpIcon className={styles.chevronIcon} />
                                    ) : (
                                        <ChevronDownIcon className={styles.chevronIcon} />
                                    )}
                                </div>

                                {isOpen && (
                                    <div className={styles.optionsList}>
                                        {loadingReasons && (
                                            <div className={styles.dropdownLoading}>Loading options...</div>
                                        )}

                                        {reasonsError && !reportReasons.length && (
                                            <div className={styles.dropdownError}>Failed to load options</div>
                                        )}

                                        {!loadingReasons && reportReasons?.length ? (
                                            reportReasons.map((reason) => {
                                                const isChecked = selectedTypeCodes.includes(reason.rrId);
                                                return (
                                                    <div
                                                        key={reason.rrId || reason.rrCode}
                                                        className={styles.optionRow}
                                                        onClick={() => toggleType(reason.rrId)}
                                                    >
                                                        <div className={`${styles.checkbox} ${isChecked ? styles.checked : ''}`}>
                                                            {isChecked && <span className={styles.checkmark}>✓</span>}
                                                        </div>
                                                        <span className={styles.optionLabel}>{reason.rrLabel}</span>
                                                    </div>
                                                );
                                            })
                                        ) : !loadingReasons ? (
                                            <div className={`${styles.optionRow} ${styles.noData}`}>No Data</div>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Suspension Duration Selector (Only for Suspend Channel) */}
                    {popupType === 'suspend_channel' && (
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>
                                {t('user:suspend_channel.duration_label', 'Suspension Duration')}
                            </label>
                            <div className={styles.durationPillsGroup}>
                                {SUSPENSION_DURATIONS.map((dur) => (
                                    <button
                                        key={dur.label}
                                        type="button"
                                        className={`${styles.durationPill} ${selectedDuration === dur.value ? styles.activePill : ''
                                            }`}
                                        onClick={() => setSelectedDuration(dur.value)}
                                    >
                                        {dur.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Message Textarea Field */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            {popupType === 'remove_content' && t('user:remove_content.reason_label', 'Removal Reason')}
                            {popupType === 'suspend_channel' && t('user:suspend_channel.reason_label', 'Suspension Reason')}
                            {popupType === 'delete_channel' && t('user:delete_channel.reason_label', 'Reason')}
                            {popupType === 'warn_creator' && t('user:warn_creator.warning_message', 'Warning Message')}{' '}
                            <span className={styles.required}>*</span>
                        </label>
                        <textarea
                            className={classNames(styles.textarea, {
                                [styles.textareaSmall]: isChannelActionPopup,
                            })}
                            placeholder="Enter message..."
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                    </div>
                </div>

                {/* Footer */}
                <div className={styles.footer}>
                    <div className={styles.footerInfo}>
                        <InfoIcon className={styles.infoIcon} />
                        <span className={styles.infoText}>
                            {popupType === 'warn_creator' ? t('This warning will be logged and the creator will be notified via email.') : t('user:common.footer_note', 'Creator will be notified via email.')}
                        </span>
                    </div>

                    <button
                        type="button"
                        className={`${styles.submitButton} ${submitConfig.styleClass} ${isFormValid() && !submitting ? styles.activeSubmit : ''
                            }`}
                        disabled={!isFormValid() || submitting}
                        onClick={handleSubmit}
                    >
                        {submitting ? 'Submitting...' : submitConfig.label}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TakeActionModal;